import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import {
  PoDisclaimer,
  PoDisclaimerGroupRemoveAction,
  PoMultiselectOption,
  PoPageAction
} from '@po-ui/ng-components';

import { MainService } from 'src/app/app.service';
import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import { getApiErrorMessage, getHttpStatus } from 'src/app/menu/smart-review/core/utils/http-error-message.util';
import {
  Period,
  defaultPeriod,
  previousPeriod,
  rehydratePeriod
} from 'src/app/menu/smart-review/core/utils/period.util';
import {
  SOURCE_PROVIDER_OPTIONS,
  sourceProviderLabel
} from 'src/app/menu/smart-review/core/utils/provider-label.util';
import {
  UI_PREF_KEYS, readPref, sanitizeStringArray, writePref
} from 'src/app/menu/smart-review/core/utils/ui-prefs.util';
import { CostsAreasService } from '../../costs-areas.service';
import { COSTS_EXPORT_LIMIT, CostsExportService } from '../../costs-export.service';
import { CostsService } from '../../costs.service';
import {
  CostBucket,
  CostDimension,
  CostPeriodFilters,
  CostPoint,
  CostSummary,
  CostTimeseries
} from '../../interfaces/costs.interface';
import {
  compareCostsDesc,
  costToChartValue,
  formatApiDate,
  formatTokens,
  sumCostStrings
} from '../../utils/costs-format.util';
import { promptTypeLabel } from '../../utils/costs-prompt-type.util';
import {
  RESOURCE_TYPE_FILTER_OPTIONS,
  isAggregateTailKey,
  resourceTypeLabel
} from '../../utils/costs-resource.util';

/**
 * O recorte guardado no navegador entre visitas. `period` vem nulo quando o que estava
 * salvo não descreve um recorte utilizável — a tela fica com o padrão de 30 dias.
 *
 * Área e usuário NÃO entram, e isso é decisão, não esquecimento. O `po-multiselect`
 * casa o model contra `p-options` no `writeValue` e, quando sobra valor sem opção
 * correspondente, ele CORRIGE o model para o que casou — `callOnChange(selectedOptions)`
 * em `po-multiselect-base`. As opções das duas chegam depois: o catálogo de áreas é uma
 * requisição à parte, e a lista de usuários é derivada da própria resposta de custos.
 * Então, no primeiro render, o campo recebia `['5e70']` com `options: []`, devolvia `[]`
 * pelo `(ngModelChange)`, disparava uma segunda carga sem filtro e — porque `load()`
 * salva — regravava a preferência vazia. O filtro não se aplicava E se apagava.
 * Guardar só o que a tela consegue restaurar sem se contradizer.
 */
interface CostsPrefs {
  period: Period | null;
  resourceTypes: string[];
  sourceProviders: string[];
}

/** O que uma carga bem-sucedida entrega. */
interface CostsLoadResult {
  summary: CostSummary;
  timeseries: CostTimeseries;
  previous: CostSummary | null;
}

/**
 * Vazios compartilhados (congelados) para os getters lidos pelo template.
 *
 * `?? []` devolveria um array NOVO a cada leitura, e o template lê a cada ciclo de
 * change detection: o `@Input()` dos filhos pareceria mudado sempre, disparando
 * `ngOnChanges` e o rebuild de categorias/séries em looping. Acontece justamente no
 * estado de erro, em que `summary`/`timeseries` viram `null` mas as seções continuam
 * renderizadas. Mesma classe de bug já resolvida em `disclaimers` e `areaRows`.
 */
const NO_BUCKETS: readonly CostBucket[] = Object.freeze([]);
const NO_POINTS: readonly CostPoint[] = Object.freeze([]);

/**
 * Painel de consumo e custo do Smart Review. Responde quatro perguntas: quanto gastamos,
 * em que evoluiu, quem/qual área gastou e qual modelo e quais reviews custam mais.
 *
 * Sem gerenciador de estado: o recorte (período + filtros) vive no componente e dispara
 * as duas chamadas (resumo + série) em paralelo.
 */
@Component({
  selector: 'app-costs-panel',
  templateUrl: './costs-panel.component.html',
  styleUrls: ['./costs-panel.component.css']
})
export class CostsPanelComponent implements OnInit, OnDestroy {
  loading = false;
  exporting = false;
  summary: CostSummary | null = null;
  timeseries: CostTimeseries | null = null;
  /** Mesmo recorte na janela anterior — base da variação exibida nos KPIs. */
  previousSummary: CostSummary | null = null;

  period: Period = defaultPeriod();

  /** Filtros aplicados ao recorte inteiro — KPIs, gráficos, tabelas e exportação. */
  selectedAreas: string[] = [];
  selectedUsers: string[] = [];
  selectedResourceTypes: string[] = [];
  selectedSourceProviders: string[] = [];

  areaOptions: PoMultiselectOption[] = [];
  userOptions: PoMultiselectOption[] = [];
  /** Fixas: são os tipos que existem, não algo que vem do breakdown do período. */
  readonly resourceTypeOptions: PoMultiselectOption[] = RESOURCE_TYPE_FILTER_OPTIONS;
  readonly sourceProviderOptions: PoMultiselectOption[] = SOURCE_PROVIDER_OPTIONS;

  /** id da área → descrição. Vazio até o cadastro do ESP responder. */
  areaNamesMap = new Map<string, string>();

  /**
   * Dimensões pedidas numa única requisição — são as que a tela renderiza.
   */
  private readonly dimensions: CostDimension[] =
    ['model', 'language', 'user', 'area', 'resource', 'resourceType', 'sourceProvider', 'promptType'];

  readonly pageActions: PoPageAction[] = [
    {
      label: 'Exportar CSV',
      icon: 'po-icon-download',
      action: () => this.exportCsv(),
      // Um período largo devolve dezenas de milhares de linhas e leva segundos; sem a
      // trava o usuário clica de novo e dispara duas vezes.
      disabled: () => this.exporting
    },
    { label: 'Atualizar', icon: 'po-icon-refresh', action: () => this.load() }
  ];

  constructor(
    private costsService: CostsService,
    private areasService: CostsAreasService,
    private exportService: CostsExportService,
    private mainService: MainService,
    private notification: NotificationServiceHM,
    private router: Router
  ) {
    // No construtor, não no ngOnInit: `load()` é público (o botão "Atualizar" e os
    // filtros chamam direto) e não pode depender de a inicialização já ter rodado para
    // ter quem o escute.
    this.watchLoadRequests();
  }

  /** Fecha as assinaturas e o agendamento na saída da tela — ver `ngOnDestroy`. */
  private readonly destroy$ = new Subject<void>();
  /** Pedidos de carga; o consumo é serializado em `watchLoadRequests`. */
  private readonly loadRequests = new Subject<void>();

  ngOnInit(): void {
    this.mainService.validSession();
    // Acesso `smartreview.admin` é garantido pelo SmartReviewAdminGuard na rota pai do hub.
    this.restorePrefs();
    this.loadAreaNames();
    this.load();
  }

  /**
   * Recorte da última visita de volta à tela, antes da primeira carga — restaurar depois
   * dispararia uma segunda requisição, com o período errado na primeira.
   *
   * Tipo de review é o único filtro restaurado porque é o único cujas opções são
   * constante da tela (`RESOURCE_TYPE_FILTER_OPTIONS`), disponíveis antes de qualquer
   * resposta. Ver `CostsPrefs` para por que área e usuário ficam de fora.
   */
  private restorePrefs(): void {
    const saved = readPref(UI_PREF_KEYS.costsFilters, (raw): CostsPrefs | null => {
      if (!raw || typeof raw !== 'object') { return null; }
      const value = raw as Record<string, unknown>;
      return {
        period: rehydratePeriod(value['period']),
        resourceTypes: sanitizeStringArray(
          value['resourceTypes'],
          this.resourceTypeOptions.map(option => String(option.value))
        ),
        sourceProviders: sanitizeStringArray(
          value['sourceProviders'],
          this.sourceProviderOptions.map(option => String(option.value))
        )
      };
    });

    if (!saved) { return; }

    if (saved.period) { this.period = saved.period; }
    this.selectedResourceTypes = saved.resourceTypes;
    this.selectedSourceProviders = saved.sourceProviders;
    this.buildDisclaimers();
  }

  private savePrefs(): void {
    writePref(UI_PREF_KEYS.costsFilters, {
      period: this.period,
      resourceTypes: this.selectedResourceTypes,
      sourceProviders: this.selectedSourceProviders
    });
  }

  /**
   * Nada pode sobreviver à saída da tela: o `setTimeout` de `scheduleLoad` dispararia
   * três requisições depois do componente destruído, e uma carga em voo aterrissaria
   * exibindo toast — ou, num 403, redirecionando — por cima da tela para a qual o
   * usuário acabou de navegar.
   */
  ngOnDestroy(): void {
    if (this.loadTimer != null) {
      window.clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currency(): string {
    return this.summary?.currency || 'USD';
  }

  /** Recorte atual, compartilhado pelas chamadas e pela exportação. */
  get filters(): CostPeriodFilters {
    const filters: CostPeriodFilters = {};
    if (this.period.dateFrom) { filters.dateFrom = this.period.dateFrom; }
    if (this.period.dateTo) { filters.dateTo = this.period.dateTo; }
    if (this.selectedAreas.length) { filters.area = [...this.selectedAreas]; }
    if (this.selectedUsers.length) { filters.user = [...this.selectedUsers]; }
    if (this.selectedResourceTypes.length) { filters.resourceType = [...this.selectedResourceTypes]; }
    if (this.selectedSourceProviders.length) { filters.sourceProvider = [...this.selectedSourceProviders]; }
    return filters;
  }

  /**
   * Filtros ativos como disclaimers do PO. A área aparece pela descrição, não pelo id,
   * porque o chip é para leitura — o valor continua sendo o id.
   *
   * Propriedade comum, não getter: `po-disclaimer-group` roda `ngDoCheck` com um
   * `IterableDiffer` a cada ciclo de change detection — um getter que faz `.map()`
   * devolveria objetos novos toda vez, e o differ interpretaria isso como "a lista
   * mudou" mesmo sem nenhuma seleção ter mudado de verdade. Cada "mudança" detectada
   * disparava `(p-change)`, que chama `onDisclaimersChange` e recarrega os dados — um
   * ciclo que se realimentava a cada change detection e multiplicava as chamadas à API
   * a cada clique num filtro (era a causa da lentidão ao filtrar). Mesma classe de bug
   * que já travava a aba com `resourceTypeRows`/`areaRows` como getters.
   */
  disclaimers: PoDisclaimer[] = [];

  private buildDisclaimers(): void {
    this.disclaimers = [
      ...this.selectedAreas.map(id => ({
        property: 'area',
        value: id,
        label: `Área: ${CostsAreasService.label(id, this.areaNamesMap)}`
      })),
      ...this.selectedUsers.map(user => ({
        property: 'user',
        value: user,
        label: `Usuário: ${user}`
      })),
      ...this.selectedResourceTypes.map(type => ({
        property: 'resourceType',
        value: type,
        label: `Tipo de review: ${resourceTypeLabel(type)}`
      })),
      ...this.selectedSourceProviders.map(provider => ({
        property: 'sourceProvider',
        value: provider,
        label: `Origem: ${sourceProviderLabel(provider)}`
      }))
    ];
  }

  /**
   * `breakdown` é omitido quando nenhuma dimensão é pedida, e a chave some sem linhas.
   * O vazio é a constante compartilhada, não um `[]` novo — ver `NO_BUCKETS`.
   */
  private bucketsOf(dimension: CostDimension): readonly CostBucket[] {
    return this.summary?.breakdown?.[dimension] ?? NO_BUCKETS;
  }

  get modelRows(): readonly CostBucket[] { return this.bucketsOf('model'); }
  get languageRows(): readonly CostBucket[] { return this.bucketsOf('language'); }
  get userRows(): readonly CostBucket[] { return this.bucketsOf('user'); }

  /**
   * Área com maior alta de custo vs. o período anterior — "o que mudou mais" pronto,
   * sem precisar vasculhar a tabela. Casa pela chave CRUA (id da área, não o rótulo
   * traduzido de `areaRows`) porque `previousSummary.breakdown.area` nunca passa por
   * `buildAreaRows` — só o vencedor é traduzido, no fim.
   *
   * Exige base de comparação (`prev > 0`) e alta de verdade (`pct > 0`): uma área nova
   * no período (sem histórico) ou em queda não é "maior alta". `(sem informação)` e
   * `(outros N)` ficam de fora — não são áreas de verdade.
   */
  get biggestAreaIncreaseLabel(): string | null {
    const current = this.bucketsOf('area').filter(bucket => !bucket.key.startsWith('('));
    const previous = (this.previousSummary?.breakdown?.area ?? []).filter(bucket => !bucket.key.startsWith('('));
    if (!current.length || !previous.length) { return null; }

    const previousCostByKey = new Map(previous.map(bucket => [bucket.key, costToChartValue(bucket.cost)]));

    let winner: { key: string; pct: number } | null = null;
    for (const bucket of current) {
      const previousCost = previousCostByKey.get(bucket.key);
      if (!previousCost) { continue; }
      const pct = Math.round(((costToChartValue(bucket.cost) - previousCost) / previousCost) * 100);
      if (pct > 0 && (!winner || pct > winner.pct)) { winner = { key: bucket.key, pct }; }
    }

    return winner ? `${CostsAreasService.label(winner.key, this.areaNamesMap)}: +${winner.pct}%` : null;
  }
  get resourceRows(): readonly CostBucket[] { return this.bucketsOf('resource'); }

  /**
   * Custo por tipo de review, tipo de prompt e por área, com a chave já traduzida
   * para exibição.
   *
   * Propriedades comuns, não getters: as demais dimensões reaproveitam o array que
   * já está em `summary.breakdown`, mas estas precisam de `.map()` para trocar a
   * chave — e um getter que mapeia devolveria um array (e objetos) novos a cada
   * checagem do Angular, não só quando o dado muda. Como o template lê isso a cada
   * ciclo de change detection, o `@Input()` da tabela pareceria mudado o tempo todo,
   * disparando `ngOnChanges` e o re-render em looping — é o que travava a aba ao abrir
   * a tabela completa. Por isso são calculadas uma vez, nos pontos em que o dado de
   * origem (`summary` ou `areaNamesMap`) realmente muda.
   */
  resourceTypeRows: CostBucket[] = [];
  sourceProviderRows: CostBucket[] = [];
  promptTypeRows: CostBucket[] = [];
  areaRows: CostBucket[] = [];

  /**
   * Chaves de `resourceType` DEPOIS da fusão de rótulos — não dá para usar
   * `breakdownKeys.resourceType`, que conta os valores crus da coluna. Com as seis
   * variantes presentes, o cru diz 6 e a tela mostra 4 linhas: a seção anunciaria
   * "As 4 de maior custo, de 6" e "Ver tabela completa (6)" com um modal de 4 linhas,
   * como se algo tivesse sido cortado. É a única dimensão em que a contagem crua e a
   * exibida divergem.
   */
  resourceTypeKeys?: number;

  /**
   * O breakdown vem da coluna crua `resource_type`, que separa as variantes de origem
   * (`comparison` × `comparison-upload`, `standalone` × `standalone-upload`) — mas as
   * duas exibem o MESMO rótulo, porque para o usuário é a mesma modalidade de review.
   * Traduzir sem somar produziria duas barras e duas linhas "Comparison", cada uma com
   * um pedaço do custo. Por isso as fatias são agrupadas pelo rótulo final.
   *
   * A cauda `(outros N)` passa direto, com a chave crua: não é um tipo, e a tabela
   * espera encontrá-la na última linha. Como a soma pode ultrapassar chaves que estavam
   * acima, o resto é reordenado por custo — a barra e o "Maior" da seção leem o topo.
   */
  private buildResourceTypeRows(): void {
    const merged = new Map<string, CostBucket>();
    const tail: CostBucket[] = [];

    for (const bucket of this.bucketsOf('resourceType')) {
      if (bucket.key.startsWith('(')) {
        if (isAggregateTailKey(bucket.key)) { tail.push(bucket); } else { merged.set(bucket.key, { ...bucket }); }
        continue;
      }

      const key = resourceTypeLabel(bucket.key);
      const current = merged.get(key);
      merged.set(key, current
        ? {
            ...current,
            cost: sumCostStrings([current.cost, bucket.cost]),
            runs: current.runs + bucket.runs,
            llmCalls: current.llmCalls + bucket.llmCalls,
            tokens: current.tokens + bucket.tokens
          }
        : { ...bucket, key });
    }

    this.resourceTypeRows = [
      ...[...merged.values()].sort((a, b) => compareCostsDesc(a.cost, b.cost)),
      ...tail
    ];

    // Com cauda, quantos rótulos as chaves somadas nela dariam é justamente o que não
    // se sabe — fica indefinido, e a seção conta pelas linhas. Um tipo de review não
    // chega perto do teto de 200 fatias, então na prática é sempre o ramo de cima.
    this.resourceTypeKeys = tail.length ? undefined : merged.size;
  }

  /** `code_review`/`technical_doc` viram o mesmo texto das abas do review. */
  private buildPromptTypeRows(): void {
    this.promptTypeRows = this.bucketsOf('promptType').map(bucket => ({
      ...bucket,
      key: bucket.key.startsWith('(') ? bucket.key : promptTypeLabel(bucket.key)
    }));
  }

  /** Origem do código revisado: TFVC/upload ou provedor git implementado. */
  private buildSourceProviderRows(): void {
    this.sourceProviderRows = this.bucketsOf('sourceProvider').map(bucket => ({
      ...bucket,
      key: bucket.key.startsWith('(') ? bucket.key : sourceProviderLabel(bucket.key)
    }));
  }

  /** Área é a única dimensão cuja chave precisa de tradução: o backend só grava o id. */
  private buildAreaRows(): void {
    this.areaRows = this.bucketsOf('area').map(bucket => ({
      ...bucket,
      key: CostsAreasService.label(bucket.key, this.areaNamesMap)
    }));
  }

  /** Chaves reais da dimensão, para a tabela avisar quando estiver mostrando um recorte. */
  keysOf(dimension: CostDimension): number | undefined {
    return this.summary?.breakdownKeys?.[dimension];
  }

  get points(): readonly CostPoint[] {
    return this.timeseries?.points ?? NO_POINTS;
  }

  /**
   * Agrupa chamadas a `load()` vindas do mesmo clique do usuário.
   *
   * O `po-listbox` (upstream, `@po-ui/ng-components`) tem `(click)` cadastrado tanto na
   * linha inteira do item quanto no `po-checkbox` interno, mais o evento nativo
   * `p-change` do próprio checkbox — um clique só na opção do multiselect dispara os
   * três. Confirmado instrumentando a página ao vivo: um clique em Área gerava 3
   * chamadas a `onAreaChange`, alternando `[valor]`/`[]`/`[valor]`, cada uma
   * recarregando os dados — a causa real da lentidão ao filtrar. Não dá para corrigir
   * isso no `node_modules`; a defesa fica do nosso lado.
   *
   * Um microtask (`Promise.resolve().then`) NÃO basta: `click` e o `change` nativo do
   * checkbox são dois eventos DOM separados, e o zone.js do Angular drena a fila de
   * microtasks entre um e outro — testado ao vivo, o microtask ainda via 3 chamadas.
   * `setTimeout(0)` é um macrotask, que só roda depois que TODOS os eventos da mesma
   * interação (clique, bubbling, change) já foram despachados e suas microtasks
   * drenadas — aí sim as três chamadas colapsam numa `load()` só.
   */
  private loadTimer: number | null = null;
  private scheduleLoad(): void {
    if (this.loadTimer != null) { return; }
    this.loadTimer = window.setTimeout(() => {
      this.loadTimer = null;
      this.load();
    });
  }

  /**
   * Uma carga por vez: o `switchMap` cancela a anterior assim que outra é pedida.
   *
   * Sem isso duas ações seguidas correm em paralelo — trocar "Todo o período" (lento,
   * histórico inteiro) por "Últimos 7 dias" (rápido) faz a resposta do recorte ANTIGO
   * chegar por último e sobrescrever a nova: a tela mostraria os números de um período
   * com o filtro exibindo outro, e a exportação (que relê `this.filters`) sairia
   * diferente do que está na tela. `scheduleLoad` não cobre esse caso — ele só agrupa
   * os eventos de um clique só, e preset/intervalo/"Atualizar" chamam `load()` direto.
   *
   * O erro é tratado DENTRO do `switchMap`: se subisse ao stream externo, o `subscribe`
   * encerraria e os filtros parariam de responder a partir da primeira falha.
   */
  private watchLoadRequests(): void {
    this.loadRequests
      .pipe(
        switchMap(() => {
          const filters = this.filters;
          return forkJoin({
            summary: this.costsService.getSummary({ ...filters, groupBy: this.dimensions }),
            timeseries: this.costsService.getTimeseries(filters),
            previous: this.loadPrevious(filters)
          }).pipe(
            map(result => ({ result: result as CostsLoadResult, error: null as unknown })),
            catchError(error => of({ result: null, error: error as unknown }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(({ result, error }) => {
        this.loading = false;
        if (result) { this.applyLoadResult(result); return; }
        this.clearLoadedData();
        this.handleCostsError(error);
      });
  }

  /**
   * O recorte é salvo aqui, e não em cada `on*Change`: toda mudança de período ou de
   * filtro termina em `load()` (direto ou por `scheduleLoad`), inclusive a remoção de
   * chips, então este é o único ponto que não dá para esquecer ao acrescentar um filtro
   * novo. O "Atualizar" também passa por aqui e regrava o mesmo recorte, sem efeito.
   */
  load(): void {
    this.savePrefs();
    this.loading = true;
    this.loadRequests.next();
  }

  private applyLoadResult(result: CostsLoadResult): void {
    this.summary = result.summary;
    this.timeseries = result.timeseries;
    this.previousSummary = result.previous;
    this.buildResourceTypeRows();
    this.buildSourceProviderRows();
    this.buildPromptTypeRows();
    this.buildAreaRows();
    this.refreshUserOptions();
  }

  private clearLoadedData(): void {
    this.summary = null;
    this.timeseries = null;
    this.previousSummary = null;
    this.resourceTypeRows = [];
    this.sourceProviderRows = [];
    this.promptTypeRows = [];
    this.areaRows = [];
  }

  /**
   * Recorte alterado no `app-period-filter`. Resolver preset, normalizar as datas do
   * datepicker e segurar o `custom` até o intervalo fechar é tudo dele — a tela só
   * recebe o período pronto e recarrega, igual à fila e às estatísticas de curadoria.
   */
  onPeriodChange(period: Period): void {
    this.period = period;
    this.load();
  }

  onAreaChange(areas: string[]): void {
    this.selectedAreas = areas ?? [];
    this.buildDisclaimers();
    this.scheduleLoad();
  }

  onUserChange(users: string[]): void {
    this.selectedUsers = users ?? [];
    this.buildDisclaimers();
    this.scheduleLoad();
  }

  onResourceTypeChange(types: string[]): void {
    this.selectedResourceTypes = types ?? [];
    this.buildDisclaimers();
    this.scheduleLoad();
  }

  onSourceProviderChange(providers: string[]): void {
    this.selectedSourceProviders = providers ?? [];
    this.buildDisclaimers();
    this.scheduleLoad();
  }

  /**
   * Remoção de um chip pelo usuário: `p-remove`, não `p-change`. `p-change` do
   * po-disclaimer-group dispara para QUALQUER alteração no array vinculado — inclusive
   * a que o próprio `buildDisclaimers()` acabou de fazer ao aplicar um filtro — e
   * escutá-lo aqui recarregava os dados de novo, por cima do reload que o filtro já
   * tinha disparado. `p-remove` só dispara quando o usuário clica no × de um chip.
   */
  onDisclaimerRemove(action: PoDisclaimerGroupRemoveAction): void {
    this.applyRemainingDisclaimers(action.currentDisclaimers);
  }

  /** Botão "remover todos": mesmo raciocínio de `onDisclaimerRemove`, sem sobra nenhuma. */
  onDisclaimersRemoveAll(): void {
    this.applyRemainingDisclaimers([]);
  }

  private applyRemainingDisclaimers(remaining: PoDisclaimer[]): void {
    this.selectedAreas = remaining.filter(d => d.property === 'area').map(d => `${d.value}`);
    this.selectedUsers = remaining.filter(d => d.property === 'user').map(d => `${d.value}`);
    this.selectedResourceTypes = remaining.filter(d => d.property === 'resourceType').map(d => `${d.value}`);
    this.selectedSourceProviders = remaining.filter(d => d.property === 'sourceProvider').map(d => `${d.value}`);
    this.buildDisclaimers();
    this.scheduleLoad();
  }

  exportCsv(): void {
    if (this.exporting) { return; }
    this.exporting = true;

    this.exportService.export(this.filters, this.period, this.currency, this.areaNamesMap)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.exporting = false;
          if (!result.rows) {
            this.notification.showToast('Nenhuma execução no recorte selecionado.', 'warning');
            return;
          }
          if (result.truncated) {
            this.notification.showToast(
              `A exportação atingiu o limite de ${formatTokens(COSTS_EXPORT_LIMIT)} linhas e está incompleta. Reduza o período.`,
              'warning',
              { durationMs: 8000 }
            );
            return;
          }
          // A instrução vem junto do download, e não fixa na tela: é aqui que ela passa
          // a ser acionável — o arquivo acabou de chegar. A explicação inteira (o que é
          // "partial", por que a soma engana) vai na primeira linha do próprio CSV, que
          // é o que sobrevive ao arquivo ser encaminhado para quem nunca viu esta tela.
          this.notification.showToast(
            `${formatTokens(result.rows)} execuções exportadas. Confira a coluna Status antes de somar o custo.`,
            'success',
            { durationMs: 8000 }
          );
        },
        error: error => {
          this.exporting = false;
          this.notification.showToast(getApiErrorMessage(error, 'Erro ao exportar os custos.'), 'error');
        }
      });
  }

  /**
   * Totais da janela anterior, para a variação. `groupBy: ['area']` é a única
   * dimensão pedida (não todas as `this.dimensions`) — é só o que "maior alta por
   * área" precisa; pedir as demais pagaria agregação que ninguém lê.
   *
   * Falha aqui não derruba a tela: a comparação é acessório, e um erro nela não pode
   * esconder os números do período atual. Sem período fechado não há o que comparar.
   */
  private loadPrevious(filters: CostPeriodFilters) {
    const previous = previousPeriod(this.period);
    if (!previous) { return of(null); }

    return this.costsService
      .getSummary({ ...filters, ...previous, groupBy: ['area'] })
      .pipe(catchError(() => of(null)));
  }

  /**
   * Catálogo de áreas do ESP: alimenta o filtro e traduz as chaves do breakdown.
   * Carrega em paralelo com os dados — o painel não espera por ele, e enquanto não
   * chega as chaves aparecem como id.
   */
  private loadAreaNames(): void {
    this.areasService.areaNames().pipe(takeUntil(this.destroy$)).subscribe(names => {
      this.areaNamesMap = names;
      this.areaOptions = CostsAreasService.toOptions(names);
      // O catálogo chega em paralelo com `load()` e pode responder depois: se o
      // summary já tiver carregado com nomes ainda vazios, os rótulos precisam ser
      // refeitos agora que os nomes existem.
      this.buildAreaRows();
      // Idem para o chip de Área: o rótulo (CostsAreasService.label) também depende
      // do catálogo, e pode ter sido montado com o mapa ainda vazio.
      this.buildDisclaimers();
    });
  }

  /**
   * Opções do filtro de usuário saem do próprio breakdown, que é a lista de quem
   * de fato consumiu no período — não há endpoint de usuários do Smart Review.
   *
   * Só é recalculada quando nenhum usuário está selecionado: com o filtro aplicado o
   * breakdown traz apenas os escolhidos, e reconstruir a lista aí deixaria o usuário
   * preso na própria seleção, sem conseguir trocar.
   */
  private refreshUserOptions(): void {
    if (this.selectedUsers.length) { return; }
    this.userOptions = this.bucketsOf('user')
      .map(bucket => bucket.key)
      // `(outros N)` é fatia agregada e `(sem informação)` não é um login: nenhum dos
      // dois serve como valor de filtro.
      .filter(key => !key.startsWith('('))
      .map(key => ({ value: key, label: key }));
  }

  private handleCostsError(error: unknown): void {
    if (getHttpStatus(error) === 403) {
      this.notification.showToast('Acesso ao painel de custos do Smart Review negado.', 'warning');
      this.router.navigate(['/menu/smart-review/reviews']);
      return;
    }
    this.notification.showToast(getApiErrorMessage(error, 'Erro ao carregar os custos.'), 'error');
  }
}
